import {PIXI} from '@colonist/pixi'
import {SpriteButtonView} from '../../ui_canvas_engine/SpriteButtonView'
import {UIGameManager} from '../UIGameManager'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {ActionsThatCanBeCancelled, ActionStatus, GameAction} from '@colonist/game-base-types'
import {UIGameHoverResourceInfoView} from '../UIGameHoverResourceInfoView'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {UIGameMechanic} from '../UIGameEventController'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils'
import {TranslationObject} from '@colonist/i18n'
import {UIGameBeginnerHintView} from '../ui_game_beginner_hints/UIGameBeginnerHintView'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {UIGameBeginnerModeActionButtonsHelper} from '../ui_game_beginner_mode/ui_game_beginner_mode_helpers/UIGameBeginnerModeActionButtonsHelper'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {CardEnum, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGameActionButtonView extends SpriteButtonView implements UIGameMechanic {
    abstract buttonSpecificEvents(data: any, eventType: GameStateEnums.UIGameEventEnums)
    repositionedButton: () => void

    protected readonly uiGameManager: UIGameManager
    protected readonly myPlayer: PlayerValidator
    protected useResources: boolean = true
    readonly preferredPosition: number
    private hoverResourceInfoLabel: UIGameHoverResourceInfoView | undefined
    protected beginnerHintView?: UIGameBeginnerHintView | undefined
    protected beginnerModeActionType: GameAction
    protected beginnerModeHelper?: UIGameBeginnerModeActionButtonsHelper

    constructor(uiGameManager: UIGameManager, preferredPosition: number, container: PIXI.Container) {
        const itemSize = uiGameManager.canvasController.itemSize
        super(0, 0, itemSize, itemSize, container)
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.myPlayer = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.myColor)

        this.preferredPosition = preferredPosition
        this.repositionedButton = () => undefined
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        this.buttonSpecificEvents(data, eventType)

        switch(eventType) {
            case UIGameEventEnums.GameStateUpdated: this.onGameStateUpdated(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            case UIGameEventEnums.CardAnimationEnd: this.beginnerModeHelper?.revealButton(); break
            default: break
        }
    }

    private onBeginnerModeStart() {
        this.beginnerModeHelper = new UIGameBeginnerModeActionButtonsHelper(this.uiGameManager, this)
        this.visible = false
    }

    protected get isBeginnerMode(): boolean {
        return this.beginnerModeHelper != undefined
    }

    protected canPerformBuyAction(actionStatus: ActionStatus): boolean {
        this.beginnerModeHelper?.triggerGuideBubbleIfCantPerform(actionStatus)
        if(!this.uiGameManager.gameController.isMyTurn) {
            new FloatingText({key: 'strings:game.errors.notYourTurn'}, this)
            return false
        }
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement) {
            new FloatingText({key: 'strings:game.errors.finishGameSetup'}, this)
            return false
        }
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.Dice) {
            const errorMessage: TranslationObject = !this.uiGameManager.gameController.diceState.diceThrown ? {key: 'strings:game.errors.rollDiceFirst'} : {key: 'strings:game.errors.finishCurrentAction'}
            new FloatingText(errorMessage, this)
            return false
        }
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase) return true
        if(!ArrayUtils.elementInArray(ActionsThatCanBeCancelled.actions, this.uiGameManager.gameController.currentState.actionState)) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, this)
            return false
        }
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Turn) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, this)
            return false
        }
        if(actionStatus.canTakeAction) return true

        const actionErrorMessage = actionStatus.errorMessageIfCantTakeAction
        if(actionErrorMessage == undefined) return false

        this.flickerCardsIfNotEnoughResources(actionErrorMessage)
        new FloatingText(actionErrorMessage, this)
        return false
    }

    protected setHoverAction(cardEnums: CardEnum[]) {
        if(this.hoverResourceInfoLabel != undefined) this.hoverResourceInfoLabel.removeHover()
        const pointerOverCallback = () => this.hoverResourceInfoLabel = new UIGameHoverResourceInfoView(cardEnums, this, this.uiGameManager.canvasController.app.stage, this.uiGameManager.cardController.getCardInventory())
        const pointerOutCallback = () => this.removeCardHover()
        this.updateHover(pointerOverCallback, pointerOutCallback)
    }

    removeCardHover() {
        if(this.hoverResourceInfoLabel == undefined) return
        this.hoverResourceInfoLabel.removeHover()
    }

    private onGameStateUpdated() {
        this.removeCardHoverIfGameOver()
        this.destroyBeginnerHintView()

        if(this.hoverResourceInfoLabel != undefined) this.hoverResourceInfoLabel.updateCardViews(this.uiGameManager.cardController.getCardInventory())
    }

    private removeCardHoverIfGameOver() {
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.GameEnd) return
        this.removeCardHover()
    }

    protected setShouldRevealButton(data: any, hiddenUIElement: HiddenUIElement, actionType: GameAction) {
        const shouldReveal: boolean = data.hiddenUIElements.includes(hiddenUIElement) == false
        const isHidden = !this.visible
        this.beginnerModeActionType = data.type

        if(!isHidden) return
        if(!shouldReveal) return
        if(data.type != actionType) {
            this.visible = true
            return
        }

        if(this.beginnerModeHelper == undefined) return
        this.beginnerModeHelper.shouldRevealButton = true
    }

    protected destroyBeginnerHintView() {
        this.beginnerHintView?.destroy()
        this.beginnerHintView = undefined
    }

    protected onBeginnerModeInteractWithGuide(data: BeginnerModeActionState, currentActionType: GameAction) {
        if(data.type != currentActionType) return
        if(!this.visible) return

        new UIGameFlickerAnimation(this).start()
    }

    private flickerCardsIfNotEnoughResources(actionErrorMessage: TranslationObject) {
        if(actionErrorMessage.key != 'strings:game.errors.notEnoughResources') return
        this.hoverResourceInfoLabel?.flickerMissingCards()
    }
}
